import { useMutation, useQuery } from '@apollo/client';
import React, { useState } from 'react';
import { BaseReact } from '../../../base.model';
import { ProfileQL } from '../../../models/Profile';
import { graphqlSchema } from '../../../services/graphql.schema';
import sharedToasterSubject from '../../../services/shared.toasterSubject';
import SectionTitleBar from '../../components/titlebar';
import Button from '../../Forms/Button';
import Input from '../../Forms/Input';
import DateInput from '../../Forms/DateInput';

import moment from 'moment';

import './Reporte.scss';
import Checkbox from '../../Forms/Checkbox';
import { ReportTemplate } from '../../../models/Report';
import TextArea from '../../Forms/TextArea';
import { FIELD_OPTIONS, NOTA_CREDITO_FIELD_OPTIONS, PEDIDOS_FIELD_OPTIONS, PRODUCTOS_FIELD_OPTIONS, SHIPPING_FIELDS_OPTIONS } from '../../../utils/report.mergeBundleToRows';

interface OrdersReporteProps extends BaseReact {
    currentUser: ProfileQL;
}

function OrdersReport({ history, location, currentUser }: OrdersReporteProps) {
    const [dateStart, setDateStart] = useState(moment().startOf('month').toDate().toJSON());
    const [dateEnd, setDateEnd] = useState(moment().endOf('month').toDate().toJSON());

    const [pedidoFields, setPedidosField] = useState<string[]>([])
    const [facturaFields, setFacturaField] = useState<string[]>([])
    const [shippingFields, setShippingField] = useState<string[]>([])

    const [reportName, setReportName] = useState('')
    const [reportDescription, setReportDescription] = useState('');

    const [reportTemplates, setReportTemplates] = useState<ReportTemplate[]>([]);

    useQuery(graphqlSchema.PROFILE.REPORTS.getTemplateReports, {
        onCompleted: ({ getTemplateReports }) => {
            console.log('getTemplateReports: ', getTemplateReports);
            setReportTemplates(JSON.parse(JSON.stringify(getTemplateReports)));
        }
    })

    const [saveReportTemplate] = useMutation(graphqlSchema.PROFILE.REPORTS.saveReportTemplate, {
        onCompleted: ({ saveReportTemplate }) => {
            console.log('REPORT SAVED: ', saveReportTemplate);
            setReportTemplates(JSON.parse(JSON.stringify(saveReportTemplate)));
            sharedToasterSubject.next({ type: 'confirm', message: 'Se actualizó su plantilla' })
        },
        onError: (e) => {
            console.error('Error saving template: ', e);
        }
    })

    const [registerReportJob] = useMutation(graphqlSchema.PROFILE.REPORTS.buildReport, {
        onCompleted: ({ buildReport }: { buildReport: string }) => {
            console.log('buildReport: ', buildReport);
            sharedToasterSubject.next({ type: 'confirm', message: 'Se registró el reporte a realizar correctamente' })
            history.push({
                pathname: '/ordenes/report',
            })
        },
        onError: (e: Error) => {
            console.log('Registering Report job ERR: ', e);
        }
    })


    const _setReportName = (value: string) => {
        if (value.length > 30) {
            return;
        } else {
            setReportName(value);
        }
    }

    const _setReportDescription = (value: string) => {
        if (value.length > 90) {
            return;
        } else {
            setReportDescription(value);
        }
    }


    const submitReportGeneration = () => {
        registerReportJob({
            variables: {
                startDate: dateStart,
                endDate: dateEnd,
                template: {
                    name: reportName,
                    userId: currentUser.amazonProfile.mwsSelledId,
                    description: reportDescription,
                    facturaFields: facturaFields,
                    pedidoFields: pedidoFields,
                    shippingFields: shippingFields
                }
            }
        })
    }

    const saveToTemplateReports = () => {
        if (reportTemplates.some(rT => rT.name.toLowerCase() === reportName.toLowerCase())) {
            // This should be a replace
            const remain = reportTemplates.filter(rT => rT.name.toLowerCase() !== reportName.toLowerCase());
            remain.push({
                name: reportName,
                userId: currentUser.amazonProfile.mwsSelledId,
                description: reportDescription,
                facturaFields: facturaFields,
                pedidoFields: pedidoFields,
                shippingFields: shippingFields
            });
            setReportTemplates([].concat(remain));
            saveReportTemplate({
                variables: {
                    template: {
                        name: reportName,
                        userId: currentUser.amazonProfile.mwsSelledId,
                        description: reportDescription,
                        facturaFields: facturaFields,
                        pedidoFields: pedidoFields,
                        shippingFields: shippingFields
                    }
                }
            })

        } else {
            const arg = [{
                name: reportName,
                userId: currentUser.amazonProfile.mwsSelledId,
                description: reportDescription,
                facturaFields: facturaFields,
                pedidoFields: pedidoFields,
                shippingFields: shippingFields
            }];
            setReportTemplates(arg.concat(reportTemplates));
            saveReportTemplate({
                variables: {
                    template: {
                        name: reportName,
                        userId: currentUser.amazonProfile.mwsSelledId,
                        description: reportDescription,
                        facturaFields: facturaFields,
                        pedidoFields: pedidoFields,
                        shippingFields: shippingFields
                    }
                }
            })
        }
    }

    const _loadReportTemplate = (reportTemplate: ReportTemplate) => () => {
        setReportName(reportTemplate.name);
        setReportDescription(reportTemplate.description);
        setFacturaField(reportTemplate.facturaFields);
        setPedidosField(reportTemplate.pedidoFields);
        setShippingField(reportTemplate.shippingFields);
    }


    const _setFacturaFields = (key: string) => (b: boolean) => {
        if (b) {
            if (!facturaFields.includes(key)) {
                const arr = [key];
                setFacturaField(arr.concat(facturaFields));
            }
        } else {
            if (!!facturaFields.includes(key)) {
                const arr = [].concat(facturaFields.filter(s => s !== key));
                setFacturaField(arr);
            }
        }
    }

    const _setPedidosField = (key: string) => (b: boolean) => {
        if (b) {
            if (!pedidoFields.includes(key)) {
                const arr = [key];
                setPedidosField(arr.concat(pedidoFields));
            }
        } else {
            if (!!pedidoFields.includes(key)) {
                const arr = [].concat(pedidoFields.filter(s => s !== key));
                setPedidosField(arr);
            }
        }
    }

    const _setShippingField = (key: string) => (b: boolean) => {
        if (b) {
            if (!shippingFields.includes(key)) {
                const arr = [key];
                setShippingField(arr.concat(shippingFields));
            }
        } else {
            if (!!shippingFields.includes(key)) {
                const arr = [].concat(shippingFields.filter(s => s !== key));
                setShippingField(arr);
            }
        }
    }

    return (
        <div id="OrdersReport">
            <SectionTitleBar currentUser={currentUser} title="Reporte de pedidos" />
            <div className="reportContent">
                <div className="filterChange">
                    <DateInput startOf="day" label="Fecha inicio" value={dateStart} placeholder="Fecha de inicio" max={dateEnd} onChange={setDateStart} />
                    <DateInput endOf="day" label="Fecha final" value={dateEnd} placeholder="Fecha final" min={dateStart} onChange={setDateEnd} />
                    <div className='_space'></div>
                    <Button primary={true} disabled={false} handleClick={submitReportGeneration}>
                        <span>Generar reporte</span>
                    </Button>
                </div>
                <div className="reportInteractionWrap">
                    <div className="card reportConfig">
                        <Input type="text" value={reportName} onChange={_setReportName} label={`Nombre del reporte ${reportName.length > 29 ? '(30 carácteres máximo)' : ''}`} placeholder="Nombre del reporte" />
                        <TextArea label={`Descripcion del reporte ${reportDescription.length > 89 ? '(90 carácteres máximo)' : ''}`} value={reportDescription} onChange={_setReportDescription} placeholder="Sin descripcion" hasError={reportDescription.length > 89} />
                        <h4>Campos de pedidos a incluir</h4>
                        <div className="fieldsChoose">
                            {PEDIDOS_FIELD_OPTIONS.map(({ label: s }) => (
                                <Checkbox label={s} key={s} value={pedidoFields.includes(s)} onChange={_setPedidosField(s)} />
                            ))}
                        </div>
                        <h4>Campos de producto a incluír</h4>
                        <div className="fieldsChoose">
                            {PRODUCTOS_FIELD_OPTIONS.map(({ label: s }) => (
                                <Checkbox label={s} key={s} value={pedidoFields.includes(s)} onChange={_setPedidosField(s)} />
                            ))}
                        </div>
                        <h4>Campos de factura a incluir</h4>
                        <div className="fieldsChoose">
                            {FIELD_OPTIONS.map(({ label, value }) => (
                                <Checkbox label={label} key={label} value={facturaFields.includes(label)} onChange={_setFacturaFields(label)} />
                            ))}
                        </div>
                        <h4>Campos de nota de crédito a incluir</h4>
                        <div className="fieldsChoose">
                            {NOTA_CREDITO_FIELD_OPTIONS.map(({ label: s }) => (
                                <Checkbox label={s} key={s} value={pedidoFields.includes(s)} onChange={_setPedidosField(s)} />
                            ))}
                        </div>
                        <h4>Campos de envio a incluir</h4>
                        <div className="fieldsChoose">
                            {SHIPPING_FIELDS_OPTIONS.map(({ label: s }) => (
                                <Checkbox label={s} key={s} value={shippingFields.includes(s)} onChange={_setShippingField(s)} />
                            ))}
                        </div>
                    </div>
                    <div className="reportFrequent card">
                        <h4>Platillas de reportes</h4>
                        <div className="reportsAvail">
                            {
                                !reportTemplates.length ?
                                    <div className="_noReports">
                                        <p>No hay plantillas de reportes guardadas</p>
                                    </div>
                                    :
                                    reportTemplates.map((rTemplate) => (
                                        <div key={rTemplate.name} className="reportTemplate" onClick={_loadReportTemplate(rTemplate)}>
                                            <p className="name">{rTemplate.name}</p>
                                            <p className="descrip">{rTemplate.description || <em>Sin descripción</em>}</p>
                                        </div>
                                    ))
                            }
                        </div>
                        <div className="action">
                            {
                                reportTemplates.length > 4 ?
                                    <Button secondary={true} disabled={true}>
                                        <span>
                                            Solo puede guardar 4 plantillas
                                        </span>
                                    </Button>

                                    :
                                    <Button secondary={true} handleClick={saveToTemplateReports} disabled={!reportName.length}>
                                        <span>
                                            Guardar reporte como plantilla
                                        </span>
                                    </Button>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>

    )
}

export default OrdersReport